import {
  Paper,
  Table,
  Badge,
  Text,
  Group,
  Tooltip,
  Code,
  Center,
  Loader,
  ActionIcon,
  ScrollArea,
  Pagination,
} from "@mantine/core";
import {
  IconExternalLink,
  IconClipboardCheck,
  IconAlertTriangle,
  IconCircleCheck,
  IconClock,
  IconTrash,
} from "@tabler/icons-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import { useAuth } from "../../../auth/AuthContext";
import { apWorkbenchApi } from "../../../api/apWorkbench.api";
import { apiError } from "../../../api/client";
import { fmtDate, statusLabel } from "./helpers";

function StatusBadge({ status }) {
  if (status === "matched")
    return (
      <Badge color="green" variant="light" leftSection={<IconCircleCheck size={12} />}>
        {statusLabel(status)}
      </Badge>
    );
  return (
    <Badge color="orange" variant="light" leftSection={<IconAlertTriangle size={12} />}>
      {statusLabel(status)}
    </Badge>
  );
}

function ReadyBadge({ ready, pushed }) {
  if (pushed) {
    return (
      <Badge color="blue" variant="filled">
        Posted
      </Badge>
    );
  }
  if (ready) {
    return <Badge color="violet" variant="light">Ready</Badge>;
  }
  return (
    <Badge color="gray" variant="light" leftSection={<IconClock size={12} />}>
      Draft
    </Badge>
  );
}

function DenormBadge({ status }) {
  const color =
    status === "ok" ? "teal" : status === "pending" ? "yellow" : "red";
  return (
    <Badge color={color} variant="light" size="sm">
      denorm: {status}
    </Badge>
  );
}

export function InvoicesTable({
  rows,
  total,
  loading,
  page,
  pageSize,
  onPageChange,
  onOpen,
}) {
  const totalPages = total ? Math.max(1, Math.ceil(total / pageSize)) : 1;
  const { user } = useAuth();
  const qc = useQueryClient();
  const isAdmin = !!user?.isAdmin;

  // Single shared mutation; we pass the id at call time. `variables` lets us
  // show the spinner on the specific row that was clicked.
  const deleteMut = useMutation({
    mutationFn: (id) => apWorkbenchApi.deleteInvoice(id),
    onSuccess: ({ id }) => {
      notifications.show({ color: "green", message: "Invoice deleted" });
      qc.removeQueries({ queryKey: ["ap-workbench", "invoice", id] });
      qc.removeQueries({ queryKey: ["ap-workbench", "images", id] });
      qc.invalidateQueries({ queryKey: ["ap-workbench", "list"] });
      qc.invalidateQueries({ queryKey: ["ap-workbench", "counts"] });
    },
    onError: (e) =>
      notifications.show({ color: "red", message: apiError(e) }),
  });

  return (
    <Paper p={0}>
      <ScrollArea>
        <Table striped highlightOnHover withTableBorder verticalSpacing="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Received</Table.Th>
              <Table.Th>Supplier</Table.Th>
              <Table.Th>GR / PO</Table.Th>
              <Table.Th>Invoice #</Table.Th>
              <Table.Th>Invoice date</Table.Th>
              <Table.Th>Lines</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Ready</Table.Th>
              <Table.Th>Denorm</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {loading ? (
              <Table.Tr>
                <Table.Td colSpan={10}>
                  <Center py="xl">
                    <Loader />
                  </Center>
                </Table.Td>
              </Table.Tr>
            ) : rows.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={10}>
                  <Center py="xl">
                    <Text size="sm" c="dimmed">
                      No invoices match these filters.
                    </Text>
                  </Center>
                </Table.Td>
              </Table.Tr>
            ) : (
              rows.map((inv) => {
                const snap = inv.grHeaderSnapshot || {};
                const c = inv.lineCounts || {};
                return (
                  <Table.Tr
                    key={inv.id}
                    style={{ cursor: "pointer" }}
                    onClick={() => onOpen?.(inv.id)}
                  >
                    <Table.Td>
                      <Text size="xs">
                        {new Date(inv.receivedAt).toLocaleString()}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{snap.cardName || "—"}</Text>
                      <Text size="xs" c="dimmed">
                        {snap.cardCode || ""}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4}>
                        <Code>{snap.docNum || inv.grDocEntry}</Code>
                        {snap.poDocNum && (
                          <Tooltip label="PO doc number">
                            <Badge size="xs" variant="dot">
                              PO {snap.poDocNum}
                            </Badge>
                          </Tooltip>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text fw={500}>{inv.invoiceNumber}</Text>
                    </Table.Td>
                    <Table.Td>{fmtDate(inv.invoiceDate)}</Table.Td>
                    <Table.Td>
                      <Group gap={4}>
                        <Tooltip label="Complete">
                          <Badge color="green" variant="light" size="sm">
                            {Number(c.complete) || 0}
                          </Badge>
                        </Tooltip>
                        {Number(c.mismatched) > 0 && (
                          <Tooltip
                            label={`${Number(c.unresolved) || 0} unresolved / ${Number(c.mismatched)} mismatched`}
                          >
                            <Badge
                              color={Number(c.unresolved) > 0 ? "orange" : "yellow"}
                              variant="light"
                              size="sm"
                            >
                              {Number(c.unresolved) || 0}/{Number(c.mismatched)}
                            </Badge>
                          </Tooltip>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <StatusBadge status={inv.status} />
                    </Table.Td>
                    <Table.Td>
                      <ReadyBadge ready={inv.readyForSap} pushed={inv.pushedToSap} />
                    </Table.Td>
                    <Table.Td>
                      <DenormBadge status={inv.denormStatus} />
                    </Table.Td>
                    <Table.Td>
                      <Group gap={2} wrap="nowrap" justify="flex-end">
                        <Tooltip label="Open invoice">
                          <ActionIcon
                            variant="subtle"
                            onClick={(e) => {
                              e.stopPropagation();
                              onOpen?.(inv.id);
                            }}
                            aria-label="Open invoice"
                          >
                            <IconExternalLink size={16} />
                          </ActionIcon>
                        </Tooltip>
                        {isAdmin && !inv.pushedToSap && (
                          <Tooltip label="Delete (admin / testing)">
                            <ActionIcon
                              variant="subtle"
                              color="red"
                              loading={
                                deleteMut.isPending &&
                                deleteMut.variables === inv.id
                              }
                              onClick={(e) => {
                                e.stopPropagation();
                                const ok = window.confirm(
                                  `Permanently delete invoice ${inv.invoiceNumber} (GR ${inv.grDocEntry})?\n\nThis cannot be undone.`
                                );
                                if (ok) deleteMut.mutate(inv.id);
                              }}
                              aria-label="Delete invoice"
                            >
                              <IconTrash size={16} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })
            )}
          </Table.Tbody>
        </Table>
      </ScrollArea>
      <Group justify="space-between" p="sm">
        <Group gap={6}>
          <IconClipboardCheck size={14} />
          <Text size="xs" c="dimmed">
            {total ?? 0} invoices • page {page} of {totalPages}
          </Text>
        </Group>
        <Pagination
          value={page}
          onChange={onPageChange}
          total={totalPages}
          size="sm"
        />
      </Group>
    </Paper>
  );
}
